import type {
  EnviarCodigo2FAPayload,
  EsqueciSenhaResponse,
  LoginPayload,
  LoginResponse,
  ValidarCodigo2FAPayload,
} from '@/types/auth'
import type { Acao } from '@/utility/permissions'
import type { PagedResult } from '@/types/api'
import type {
  DefinirPermissoesPayload,
  LookupItem,
  PermissaoOption,
  PermissoesCredencial,
} from '@/types/lookup'
import type {
  AlertaDashboard,
  IndicadoresDashboard,
  PreferenciaIndicadoresPayload,
  SaudeProduto,
} from '@/types/dashboard'
import type {
  Credencial,
  CredencialResumo,
  ListarCredenciaisFiltro,
  PlanoCredencial,
  ProcedimentoTabelaPropria,
} from '@/types/credencial'
import type {
  AtualizarOperadorPayload,
  CriarOperadorPayload,
  ListarOperadoresQuery,
  OperadorResumo,
} from '@/types/operador'
import type {
  CancelamentoManualPayload,
  CancelamentoPayload,
  GuiaCompleta,
  GuiaResumida,
  HistoricoGuiaEvento,
  ListarGuiasFiltro,
} from '@/types/guia'
import type {
  AdministradorResumo,
  AtualizarAdministradorPayload,
  CriarAdministradorPayload,
  ListarAdministradoresQuery,
} from '@/types/administrador'
import type {
  InterfaceAutorizacao,
  ListarTiposCredenciaisFiltro,
  SalvarTipoCredencialPayload,
  TipoCredencial,
  TipoCredencialResumo,
} from '@/types/tipo-credencial'
import type { GerarRelatorioFiltro, RelatorioArquivo } from '@/types/relatorio'
import { downloadArquivo, request } from './api'

export const AuthApi = {
  login: (body: LoginPayload) =>
    request<LoginResponse>({
      method: 'POST',
      path: '/v2/auth-backoffice/login',
      auth: false,
      body,
    }),

  enviarCodigo: (body: EnviarCodigo2FAPayload) =>
    request<boolean>({
      method: 'POST',
      path: '/v2/auth-backoffice/enviar-codigo-2fa',
      auth: false,
      body,
    }),

  validarCodigo: (body: ValidarCodigo2FAPayload) =>
    request<LoginResponse>({
      method: 'POST',
      path: '/v2/auth-backoffice/validar-codigo-2fa',
      auth: false,
      body,
    }),

  refreshToken: (refreshToken: string) =>
    request<LoginResponse>({
      method: 'POST',
      path: '/v2/auth-backoffice/refresh-token',
      auth: false,
      body: { refreshToken },
    }),

  esqueciMinhaSenha: (email: string) =>
    request<EsqueciSenhaResponse>({
      method: 'POST',
      path: '/v2/auth-backoffice/esqueci-minha-senha',
      auth: false,
      body: { email },
    }),
}

export const GuiasApi = {
  listar: (filtro: ListarGuiasFiltro, signal?: AbortSignal) =>
    request<PagedResult<GuiaResumida>>({
      path: '/v2/guias',
      query: { ...filtro },
      signal,
    }),

  obter: (guiaId: string, signal?: AbortSignal) =>
    request<GuiaCompleta>({ path: `/v2/guias/${guiaId}`, signal }),

  historico: (guiaId: string, pageNumber: number, pageSize: number, signal?: AbortSignal) =>
    request<PagedResult<HistoricoGuiaEvento>>({
      path: `/v2/guias/${guiaId}/historico`,
      query: { pageNumber, pageSize },
      signal,
    }),

  payloadHistorico: (guiaId: string, logId: string, signal?: AbortSignal) =>
    request<string>({
      path: `/v2/guias/${guiaId}/historico/${logId}/payload`,
      signal,
    }),

  cancelar: (body: CancelamentoPayload) =>
    request<boolean>({ method: 'POST', path: '/v2/guias/cancelamento', body }),

  cancelarManual: (body: CancelamentoManualPayload) =>
    request<boolean>({ method: 'POST', path: '/v2/guias/cancelamento-manual', body }),
}

export const CredenciaisApi = {
  listar: (filtro: ListarCredenciaisFiltro, signal?: AbortSignal) =>
    request<PagedResult<CredencialResumo>>({
      method: 'POST',
      path: '/v2/credenciais-backoffice/listar-credenciais',
      body: filtro,
      signal,
    }),

  obter: (credenciaisId: string, signal?: AbortSignal) =>
    request<Credencial>({ path: `/v2/credenciais-backoffice/${credenciaisId}`, signal }),

  atualizar: (body: Credencial) =>
    request<Credencial>({
      method: 'PUT',
      path: '/v2/credenciais-backoffice/update-credencial',
      body,
    }),

  atualizarSituacao: (credenciaisId: string, ativa: boolean) =>
    request<boolean>({
      method: 'PUT',
      path: `/v2/credenciais-backoffice/${credenciaisId}/situacao/${ativa}`,
    }),

  definirTiposArquivosDoTipo: (tipoCredencialId: string, tiposArquivosIds: string[]) =>
    request<boolean>({
      method: 'PUT',
      path: `/v2/credenciais-backoffice/tipos-credenciais/${tipoCredencialId}/tipos-arquivos`,
      body: tiposArquivosIds,
    }),
}

export const DashboardApi = {
  indicadores: (signal?: AbortSignal) =>
    request<IndicadoresDashboard>({ path: '/v2/dashboard/indicadores', signal }),

  saude: (signal?: AbortSignal) =>
    request<SaudeProduto[]>({ path: '/v2/dashboard/saude', signal }),

  alertas: (signal?: AbortSignal) =>
    request<AlertaDashboard[]>({ path: '/v2/dashboard/alertas', signal }),

  preferenciaIndicadores: (perfil: string | undefined, signal?: AbortSignal) =>
    request<string[]>({
      path: '/v2/dashboard/preferencias/indicadores',
      query: { perfil },
      signal,
    }),

  atualizarPreferenciaIndicadores: (body: PreferenciaIndicadoresPayload) =>
    request<boolean>({
      method: 'PUT',
      path: '/v2/dashboard/preferencias/indicadores',
      body,
    }),
}

export const PermissoesApi = {
  catalogo: (signal?: AbortSignal) =>
    request<Acao[]>({ path: '/v1/backoffice/permissoes/catalogo', signal }),

  obter: (usuarioBackofficeId: string, signal?: AbortSignal) =>
    request<Acao[]>({ path: `/v1/backoffice/permissoes/${usuarioBackofficeId}`, signal }),

  definir: (usuarioBackofficeId: string, acoes: Acao[]) =>
    request<null>({
      method: 'PUT',
      path: `/v1/backoffice/permissoes/${usuarioBackofficeId}`,
      body: { acoes },
    }),
}
const LIMITE_LOOKUP_SEM_BUSCA = 50

export const RelatoriosApi = {
  download: (clienteId: string, filtro: GerarRelatorioFiltro, signal?: AbortSignal) =>
    request<RelatorioArquivo>({
      method: 'POST',
      path: `/v2/relatorios/${clienteId}/download`,
      body: filtro,
      signal,
    }),
}

export const LookupsApi = {
  clientes: (termo: string, signal?: AbortSignal) =>
    request<LookupItem[]>({ path: '/v2/lookups/clientes', query: { termo }, signal }),

  operadoras: (termo: string, signal?: AbortSignal) =>
    request<LookupItem[]>({ path: '/v2/lookups/operadoras', query: { termo }, signal }),

  credenciais: (termo: string, clienteId: string | null, signal?: AbortSignal) =>
    request<LookupItem[]>({ path: '/v2/lookups/credenciais', query: { termo, clienteId }, signal }),

  especialidades: (termo: string, signal?: AbortSignal) =>
    request<LookupItem[]>({ path: '/v2/lookups/especialidades', query: { termo }, signal }),

  conselhos: (signal?: AbortSignal) =>
    request<LookupItem[]>({ path: '/v2/lookups/conselhos', signal }),

  profissionais: (clienteId: string, termo: string, signal?: AbortSignal) =>
    request<LookupItem[]>({
      path: '/v2/lookups/profissionais',
      query: { clienteId, termo },
      signal,
    }),

  tiposToken: (signal?: AbortSignal) =>
    request<LookupItem[]>({ path: '/v2/lookups/tipos-token', signal }),

  tiposCredenciais: (operadoraId: string | null, signal?: AbortSignal) =>
    request<LookupItem[]>({
      path: '/v2/lookups/tipos-credenciais',
      query: { operadoraId, limite: LIMITE_LOOKUP_SEM_BUSCA },
      signal,
    }),

  tiposArquivos: (signal?: AbortSignal) =>
    request<LookupItem[]>({ path: '/v2/lookups/tipos-arquivos', signal }),
}

export const PermissoesCredencialApi = {
  obter: (credencialId: string, signal?: AbortSignal) =>
    request<PermissoesCredencial>({
      path: `/v2/credenciais-backoffice/${credencialId}/permissoes`,
      signal,
    }),

  definir: (credencialId: string, body: DefinirPermissoesPayload) =>
    request<boolean>({
      method: 'PUT',
      path: `/v2/credenciais-backoffice/${credencialId}/permissoes`,
      body,
    }),

  usuariosDisponiveis: (credencialId: string, termo: string, signal?: AbortSignal) =>
    request<PermissaoOption[]>({
      path: `/v2/credenciais-backoffice/${credencialId}/usuarios-disponiveis`,
      query: { termo },
      signal,
    }),

  unidadesDisponiveis: (credencialId: string, termo: string, signal?: AbortSignal) =>
    request<PermissaoOption[]>({
      path: `/v2/credenciais-backoffice/${credencialId}/unidades-disponiveis`,
      query: { termo },
      signal,
    }),
}

export const TiposCredenciaisApi = {
  listar: (filtro: ListarTiposCredenciaisFiltro, signal?: AbortSignal) =>
    request<PagedResult<TipoCredencialResumo>>({
      path: '/v2/tipos-credenciais-backoffice',
      query: { ...filtro },
      signal,
    }),

  obter: (tipoCredencialId: string, signal?: AbortSignal) =>
    request<TipoCredencial>({
      path: `/v2/tipos-credenciais-backoffice/${tipoCredencialId}`,
      signal,
    }),

  criar: (body: SalvarTipoCredencialPayload) =>
    request<TipoCredencial>({
      method: 'POST',
      path: '/v2/tipos-credenciais-backoffice',
      body,
    }),

  atualizar: (tipoCredencialId: string, body: SalvarTipoCredencialPayload) =>
    request<TipoCredencial>({
      method: 'PUT',
      path: `/v2/tipos-credenciais-backoffice/${tipoCredencialId}`,
      body,
    }),

  interfaces: (signal?: AbortSignal) =>
    request<InterfaceAutorizacao[]>({
      path: '/v2/tipos-credenciais-backoffice/interfaces',
      signal,
    }),
}

export const PlanosApi = {
  listar: (credencialId: string, pageNumber: number, pageSize: number, signal?: AbortSignal) =>
    request<PagedResult<PlanoCredencial>>({
      path: `/v2/credenciais-backoffice/${credencialId}/planos`,
      query: { pageNumber, pageSize },
      signal,
    }),

  criar: (credencialId: string, body: { descricao: string; codigo: string | null }) =>
    request<PlanoCredencial>({
      method: 'POST',
      path: `/v2/credenciais-backoffice/${credencialId}/planos`,
      body,
    }),

  atualizar: (
    credencialId: string,
    planoId: string,
    body: { descricao: string; codigo: string | null },
  ) =>
    request<PlanoCredencial>({
      method: 'PUT',
      path: `/v2/credenciais-backoffice/${credencialId}/planos/${planoId}`,
      body,
    }),

  excluir: (credencialId: string, planoId: string) =>
    request<boolean>({
      method: 'DELETE',
      path: `/v2/credenciais-backoffice/${credencialId}/planos/${planoId}`,
    }),
}

export const TabelaPropriaApi = {
  listar: (
    credencialId: string,
    busca: string,
    pageNumber: number,
    pageSize: number,
    signal?: AbortSignal,
  ) =>
    request<PagedResult<ProcedimentoTabelaPropria>>({
      path: `/v2/credenciais-backoffice/${credencialId}/tabela`,
      query: { busca, pageNumber, pageSize },
      signal,
    }),

  importar: (credencialId: string, base64: string, name: string, type: string) =>
    request<boolean>({
      method: 'POST',
      path: `/v2/credenciais-backoffice/${credencialId}/tabela`,
      body: { base64, name, type },
    }),

  alterarSituacao: (credencialId: string, procedimentoId: string, ativo: boolean) =>
    request<ProcedimentoTabelaPropria>({
      method: 'PUT',
      path: `/v2/credenciais-backoffice/${credencialId}/tabela/${procedimentoId}/situacao/${ativo}`,
    }),

  baixar: (credencialId: string, nomeArquivoPadrao: string) =>
    downloadArquivo({
      path: `/v2/credenciais-backoffice/${credencialId}/tabela/download`,
      nomeArquivoPadrao,
    }),
}

export const OperadoresApi = {
  listar: (query: ListarOperadoresQuery, signal?: AbortSignal) =>
    request<PagedResult<OperadorResumo>>({
      path: '/v2/backoffice/operador',
      query: { ...query },
      signal,
    }),

  criar: (body: CriarOperadorPayload) =>
    request<OperadorResumo>({
      method: 'POST',
      path: '/v2/backoffice/operador',
      body,
    }),

  atualizar: (body: AtualizarOperadorPayload) =>
    request<OperadorResumo>({
      method: 'POST',
      path: '/v2/backoffice/operador/atualizar',
      body,
    }),

  inativar: (operadorId: string) =>
    request<boolean>({
      method: 'POST',
      path: `/v2/backoffice/operador/${operadorId}/inativar`,
    }),

  reativar: (operadorId: string) =>
    request<boolean>({
      method: 'POST',
      path: `/v2/backoffice/operador/${operadorId}/reativar`,
    }),

  resetarSenha: (operadorId: string) =>
    request<boolean>({
      method: 'POST',
      path: `/v2/backoffice/operador/${operadorId}/resetar-senha`,
    }),
}

export const AdministradoresApi = {
  listar: (query: ListarAdministradoresQuery, signal?: AbortSignal) =>
    request<PagedResult<AdministradorResumo>>({
      path: '/v2/backoffice/admin-user',
      query: { ...query },
      signal,
    }),

  criar: (body: CriarAdministradorPayload) =>
    request<AdministradorResumo>({
      method: 'POST',
      path: '/v2/backoffice/admin-user',
      body,
    }),

  atualizar: (body: AtualizarAdministradorPayload) =>
    request<AdministradorResumo>({
      method: 'POST',
      path: '/v2/backoffice/admin-user/atualizar',
      body,
    }),

  inativar: (administradorId: string) =>
    request<boolean>({
      method: 'POST',
      path: `/v2/backoffice/admin-user/${administradorId}/inativar`,
    }),

  reativar: (administradorId: string) =>
    request<boolean>({
      method: 'POST',
      path: `/v2/backoffice/admin-user/${administradorId}/reativar`,
    }),

  resetarSenha: (administradorId: string) =>
    request<boolean>({
      method: 'POST',
      path: `/v2/backoffice/admin-user/${administradorId}/resetar-senha`,
    }),
}
